import { ChangeDetectionStrategy, Component, ElementRef, Signal, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { MarkdownComponent } from 'ngx-markdown';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdProfile } from '../basic-properties/hd-chart-properties.types';
import { HD_PROFILE_LINE_KEYS } from '../basic-properties/hd-chart-properties.constants';
import {
  HD_PROFILE_DEFINITIONS,
  HD_PROFILE_ROLE_LINES,
  hdProfileKey,
  hdProfileLabel,
} from './hd-profile-info-metadata';
import { HdProfileInfoService } from './hd-profile-info.service';

/** Paneel-data: het profiel van de actieve chart voedt de "jouw profiel"-markeringen. */
export interface HdProfileInfoPanelData {
  readonly profile: HdProfile | null;
}

/** Snelkoppeling-chip bovenaan: naar het eigen profiel of een eigen rollen-lijn. */
interface OwnChipVm {
  /** `data-profile-key` (profiel) of `data-roles-line` (rollen-lijn) om naartoe te scrollen. */
  readonly targetKey: string;
  readonly label: string;
}

interface ProfileSectionVm {
  readonly key: string;
  /** "4/6". */
  readonly label: string;
  /** Lijnnamen, bv. "Opportunist / Rolmodel"; leeg tot de vertaling geladen is. */
  readonly subtitle: string;
  readonly url: string;
  readonly isOwn: boolean;
  readonly ownBadge: string;
}

interface RolesLineSectionVm {
  readonly line: number;
  /** "Lijn 4". */
  readonly label: string;
  /** Lijnnaam, bv. "Netwerker / Opportunist". */
  readonly subtitle: string;
  readonly url: string;
  /** "Personaliteitslijn 4" / "Designlijn 6, onbewust"-badges (0, 1 of 2 stuks). */
  readonly ownBadges: readonly string[];
}

const PROPERTIES_PREFIX = 'humanDesign.properties';

/**
 * Gedockt/zwevend info-paneel met de profielen en genetische rollen: eerst het
 * algemene profiel-document, dan één sectie per profiel, dan de rollen-intro en
 * één sectie per rollen-lijn. De markdown-documenten komen per contenttaal uit
 * `assets/docs/human-design/profiles/` (zelfde patroon als
 * {@link HdNodalEnvironmentsInfoPanelComponent}).
 *
 * Standaard toont de profielenlijst enkel het eigen profiel en de rollenlijst
 * enkel de eigen personaliteits- en designlijn (personaliteit eerst, conform
 * het rapport: eerst focus op de personaliteitslijn); schakelknoppen tonen
 * alles. Zonder profiel-data vallen beide lijsten terug op alles.
 */
@Component({
  selector: 'hd-profile-info-panel',
  standalone: true,
  imports: [MarkdownComponent],
  templateUrl: './hd-profile-info-panel.component.html',
  styleUrl: './hd-profile-info-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdProfileInfoPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdProfileInfoPanelData | null> | null;
  private readonly infoService = inject(HdProfileInfoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  private readonly ownProfile = computed<HdProfile | null>(() => this.panelData?.()?.profile ?? null);

  protected readonly title = computed(() => this.text('title'));

  protected readonly generalUrl = computed(() => this.infoService.markdownUrl('general', this.lang()));

  protected readonly rolesTitle = computed(() => this.text('rolesTitle'));

  protected readonly rolesGeneralUrl = computed(() =>
    this.infoService.markdownUrl('roles-general', this.lang()),
  );

  protected readonly ownLabel = computed(() => this.text('ownLabel'));

  protected readonly jumpToOwnLabel = computed(() => this.text('jumpToOwnLabel'));

  protected readonly hasOwnProfile = computed(() => this.ownProfile() != null);

  /** Alle 12 profielen tonen (true) of enkel het eigen profiel (false, default). */
  protected readonly showAllProfiles = signal(false);

  /** Alle 6 rollen-lijnen tonen (true) of enkel de eigen lijnen (false, default). */
  protected readonly showAllRoleLines = signal(false);

  protected readonly profilesToggleLabel = computed(() =>
    this.text(this.showAllProfiles() ? 'showOwnLabel' : 'showAllLabel'),
  );

  protected readonly rolesToggleLabel = computed(() =>
    this.text(this.showAllRoleLines() ? 'rolesShowOwnLabel' : 'rolesShowAllLabel'),
  );

  /** Snelkoppelingen: eigen profiel + rollen van de personaliteits- en designlijn. */
  protected readonly ownChips = computed<readonly OwnChipVm[]>(() => {
    const own = this.ownProfile();
    if (!own) {
      return [];
    }
    const chips: OwnChipVm[] = [{
      targetKey: `[data-profile-key="${own.personalityLine}-${own.designLine}"]`,
      label: this.text('ownProfileChipLabel')
        .replaceAll('{personality}', String(own.personalityLine))
        .replaceAll('{design}', String(own.designLine)),
    }, {
      targetKey: `[data-roles-line="${own.personalityLine}"]`,
      label: this.lineTemplate('personalityLineChipLabel', own.personalityLine),
    }];
    if (own.designLine !== own.personalityLine) {
      chips.push({
        targetKey: `[data-roles-line="${own.designLine}"]`,
        label: this.lineTemplate('designLineChipLabel', own.designLine),
      });
    }
    return chips;
  });

  private readonly profileSections = computed<readonly ProfileSectionVm[]>(() => {
    const lang = this.lang();
    const own = this.ownProfile();
    return HD_PROFILE_DEFINITIONS.map(profile => ({
      key: hdProfileKey(profile),
      label: hdProfileLabel(profile),
      subtitle: `${this.lineName(profile.personality, lang)} / ${this.lineName(profile.design, lang)}`,
      url: this.infoService.markdownUrl(hdProfileKey(profile), lang),
      isOwn: own != null && own.personalityLine === profile.personality && own.designLine === profile.design,
      ownBadge: this.text('ownBadgeLabel'),
    }));
  });

  /** De gerenderde profielsecties: standaard enkel het eigen profiel; zonder data alles. */
  protected readonly visibleProfileSections = computed<readonly ProfileSectionVm[]>(() => {
    const sections = this.profileSections();
    if (this.showAllProfiles() || !this.hasOwnProfile()) {
      return sections;
    }
    return sections.filter(section => section.isOwn);
  });

  private readonly rolesLineSections = computed<readonly RolesLineSectionVm[]>(() => {
    const lang = this.lang();
    const own = this.ownProfile();
    return HD_PROFILE_ROLE_LINES.map(line => {
      const ownBadges: string[] = [];
      if (own?.personalityLine === line) {
        ownBadges.push(this.lineTemplate('personalityLineBadgeLabel', line));
      }
      if (own?.designLine === line) {
        ownBadges.push(this.lineTemplate('designLineBadgeLabel', line));
      }
      return {
        line,
        label: this.lineTemplate('lineSectionLabel', line),
        subtitle: this.lineName(line, lang),
        url: this.infoService.markdownUrl(`roles-line-${line}`, lang),
        ownBadges,
      };
    });
  });

  /**
   * De gerenderde rollen-lijnen: standaard enkel de eigen lijnen, met de
   * personaliteitslijn eerst (het rapport legt de focus eerst daarop en pas
   * daarna op de onbewuste designlijn); volledig zicht in canonieke volgorde.
   */
  protected readonly visibleRolesLineSections = computed<readonly RolesLineSectionVm[]>(() => {
    const sections = this.rolesLineSections();
    const own = this.ownProfile();
    if (this.showAllRoleLines() || !own) {
      return sections;
    }
    const ownSections = sections.filter(section => section.ownBadges.length > 0);
    return [...ownSections].sort((a, b) =>
      Number(b.line === own.personalityLine) - Number(a.line === own.personalityLine));
  });

  protected toggleShowAllProfiles(): void {
    this.showAllProfiles.update(showAll => !showAll);
  }

  protected toggleShowAllRoleLines(): void {
    this.showAllRoleLines.update(showAll => !showAll);
  }

  protected scrollTo(selector: string): void {
    const target = this.host.nativeElement.querySelector(selector);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Knop naast de titel: spring voorbij de intro naar de "jouw profiel"-strook. */
  protected scrollToOwn(): void {
    this.scrollTo('.hd-profile-info__own');
  }

  /** Lijnnaam ("Onderzoeker") uit de properties-vertaling, in de contenttaal. */
  private lineName(line: number, lang: string): string {
    const key = HD_PROFILE_LINE_KEYS[line - 1];
    const path = `${PROPERTIES_PREFIX}.profileLineNames.${key}`;
    const translated = this.transloco.translate(path, undefined, lang);
    return translated === path ? '' : translated;
  }

  /** Scope-template met een `{line}`-placeholder. */
  private lineTemplate(key: string, line: number): string {
    return this.text(key).replaceAll('{line}', String(line));
  }

  private text(key: string): string {
    return this.gateInfo.profilesInfoTextIn(key, this.lang());
  }
}
